import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiClipboardAlertOutline } from '@mdi/js'
import { $api } from '../../../api/queryClient'
import { useMemberKind, usePermissions } from '../../../contexts/PermissionsContext'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../components/shared/PageHeader'
import { EmptyState } from '../../../components/ui/empty-state'
import { Badge } from '../../../components/ui/badge'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { shellForKind } from '../../../lib/sidebar-menus'
import { formatIssueReference } from '../../../lib/issue-reference'

// The Tickets and Steering menus are one now: the unified master-detail queue
// lives at /$org/steering, and staff opening the old list land there. A
// resident has no steering console: this route is their "My requests"
// (spec 2026-10-07-03), the issues the API lets them see.
export const Route = createFileRoute('/$org/issues/')({
    component: IssuesIndex,
})

function IssuesIndex() {
    const { org } = Route.useParams()
    const kind = useMemberKind()
    const { isLoading } = usePermissions()

    if (isLoading && kind === undefined) {
        return null
    }
    if (shellForKind(kind).myPlacesBreadcrumb) {
        return <MyRequests org={org} />
    }

    return (
        <Navigate
            to="/$org/steering"
            params={{ org }}
            search={{ category: undefined, view: undefined, selected: undefined, subcategory: undefined, asset: undefined, subtree: undefined, due: undefined, status: undefined, sort: undefined, q: undefined, page: undefined, tab: undefined, nature: undefined }}
            replace
        />
    )
}

function MyRequests({ org }: { org: string }) {
    const { t } = useTranslation()
    const { t: tIssues, i18n } = useTranslation('issues')

    useBreadcrumbs([
        { title: t('nav.my_places'), path: `/${org}` },
        { title: t('nav.my_requests') },
    ])

    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues', {
        params: {
            path: { orgCode: org },
            query: {
                status: 'open,in_progress,closed',
                assignee_uid: 'all',
                limit: 100,
            } as unknown as Record<string, unknown>,
        },
    })
    const issues = data?.data ?? []

    return (
        <div data-testid="my-requests">
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.requests}
                title={t('nav.my_requests')}
                description={tIssues('my_requests.description')}
            />
            {isLoading ? (
                <p className="text-sm text-muted-foreground">{tIssues('list.loading')}</p>
            ) : issues.length === 0 ? (
                <EmptyState
                    icon={mdiClipboardAlertOutline}
                    title={tIssues('my_requests.empty')}
                    description={tIssues('my_requests.empty_hint')}
                    data-testid="my-requests-empty"
                />
            ) : (
                <div className="divide-y divide-border rounded-md border border-border bg-card">
                    {issues.map((issue) => {
                        const reference = formatIssueReference((issue as { number?: number }).number, i18n.language)
                        return (
                            <Link
                                key={issue.uid}
                                to="/$org/issues/$uid"
                                params={{ org, uid: issue.uid }}
                                className="flex items-center gap-3 px-3 py-3 hover:bg-muted/50"
                                data-testid={`my-request-${issue.uid}`}
                            >
                                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                                    {reference && (
                                        <span className="mr-1.5 font-normal tabular-nums text-muted-foreground">{reference}</span>
                                    )}
                                    {issue.title}
                                </span>
                                <Badge variant={issue.status === 'closed' ? 'secondary' : 'outline'} className="shrink-0">
                                    {tIssues(`status.${issue.status}`)}
                                </Badge>
                                {issue.created_at && (
                                    <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                                        {new Date(issue.created_at).toLocaleDateString(i18n.language)}
                                    </span>
                                )}
                            </Link>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
